import { useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card, Tag, Typography, Row, Col, Spinner, Space, Button, Tooltip, StatCard,
  Avatar, theme, Result, EmptyState, message, cn, type TagTone,
} from '../../ui';
import {
  Phone, MessageCircle, Mail, Pencil, User, MapPin, Star, CalendarClock,
  ShieldCheck, Contact, Clock, Smartphone, CheckCircle2, AlertTriangle, Lock,
} from 'lucide-react';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermissionChecker } from '../../store/auth';
import { UserFormModal } from './UserFormModal';
import { AttendanceUserDrawer, type AttUser } from '../ffa/AttendanceUserDrawer';
import { StaffMoneyCard } from './StaffMoneyCard';
import { InlineEditField } from '../../components/InlineEditField';
import { DocumentPreview } from '../../components/DocumentPreview';
import { displayMobile, waNumber } from '../../lib/validators';
import { formatIst, formatIstDate } from '../../lib/time';
import { LANGUAGE_NAME } from '../../constants/languages';

const ROLE_COLOR: Record<string, TagTone> = {
  SUPER_ADMIN: 'red', ADMIN: 'red', ACCOUNTS: 'gold',
  SALES_HEAD: 'purple', SALES_PERSON: 'blue', WAREHOUSE_MANAGER: 'cyan',
};
const STATUS_COLOR: Record<string, TagTone> = { active: 'green', suspended: 'red', locked: 'gold' };
const STATUS_LABEL: Record<string, string> = { active: 'Active', suspended: 'Suspended', locked: 'Locked' };
// Only field staff have a territory book / customer ledger worth surfacing as KPIs — an Accountant or
// Warehouse Manager would show four zeroes, which reads as missing data rather than "not applicable".
const FIELD_ROLES = ['SALES_PERSON', 'SALES_HEAD'];
const LEVEL_TONE: Record<string, TagTone> = { zone: 'purple', state: 'blue', district: 'cyan', beat: 'green' };

// Staff roles only — RETAILER/FARMER logins are provisioned through the Retailer/Farmer flows.
// Same list (and labels) as the user form; the backend's STAFF_ROLES is the enforced copy.
const ROLE_OPTIONS = [
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'ACCOUNTS', label: 'Accounts' },
  { value: 'WAREHOUSE_MANAGER', label: 'Warehouse Manager' },
  { value: 'SALES_HEAD', label: 'Sales Head' },
  { value: 'SALES_PERSON', label: 'Sales Person' },
];
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' }, { value: 'hi', label: 'हिंदी' }, { value: 'mr', label: 'मराठी' },
  { value: 'bn', label: 'বাংলা' }, { value: 'or', label: 'ଓଡ଼ିଆ' },
];

/**
 * Who each role reports up to — the org hierarchy the user form already enforces.
 *
 * A role that is ABSENT here has no reporting line at all: a Super Admin is the top, and a
 * Warehouse Manager answers through their warehouse rather than the org chart. Those get a locked
 * field that says so, rather than a picker whose value the form would clear on its next save.
 */
const REPORTS_TO: Record<string, { managerLabel: string; managerRoles: string[] }> = {
  SALES_PERSON: { managerLabel: 'Sales Head', managerRoles: ['SALES_HEAD'] },
  SALES_HEAD: { managerLabel: 'Admin', managerRoles: ['ADMIN'] },
  ACCOUNTS: { managerLabel: 'Admin / Super Admin', managerRoles: ['ADMIN', 'SUPER_ADMIN'] },
  ADMIN: { managerLabel: 'Super Admin', managerRoles: ['SUPER_ADMIN'] },
};

/** Section header for the profile — a tinted rounded icon chip + label, matching the retailer detail page. */
function SectionH({ icon, tint, children }: { icon: ReactNode; tint: string; children: ReactNode }) {
  return (
    <h4 className="mb-4 flex items-center gap-2.5">
      <span className={`flex size-7 shrink-0 items-center justify-center rounded-lg ${tint}`}>{icon}</span>
      <span className="type-overline text-content">{children}</span>
    </h4>
  );
}
/** One label/value pair — a small subtle label above a medium-weight value; muted em-dash when empty. */
function OField({ label, children, className }: { label: string; children?: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <dt className="type-caption text-subtle">{label}</dt>
      <dd className="mt-1 type-body-strong text-content">{children ?? <span className="font-normal text-subtle">—</span>}</dd>
    </div>
  );
}

/** One territory binding row — ⭐ marks a territory the staff member OWNS (is_primary). */
function TerrRow({ a, owned }: { a: any; owned?: boolean }) {
  return (
    <li className="flex items-center gap-2.5 rounded-control border border-line bg-surface-2/40 px-3 py-2">
      {owned
        ? <Star className="size-3.5 shrink-0 fill-amber-400 text-amber-500" />
        : <MapPin className="size-3.5 shrink-0 text-subtle" />}
      <span className="min-w-0 flex-1 truncate type-body-medium text-content">
        <span className="font-mono text-xs text-muted">{a.code}</span> · {a.name}
      </span>
      <Tag tone={LEVEL_TONE[a.level] ?? 'neutral'} style={{ margin: 0 }}>{a.level}</Tag>
    </li>
  );
}

/**
 * WHETHER THIS ACCOUNT IS ACTUALLY GOOD — a verdict, not a coloured word.
 *
 * "Active" in a green pill is a database column. It does not tell the reader that a locked account
 * clears itself after fifteen minutes, that a suspended one already released its territories, or
 * that an account nobody has ever signed into is not the same thing as a working one. Each of those
 * is a different problem with a different next step, so each gets said in words.
 *
 * The three states of the `users.status` column are not enough on their own: `active` covers both
 * "signs in every day" and "we created this login in March and nobody ever used it", and a live
 * `locked_until` blocks sign-in whether or not the status column was ever flipped.
 */
function accountHealth(user: any): {
  tone: 'good' | 'warn' | 'bad'; label: string; title: string; detail: string;
} {
  const lockedUntil = user.lockedUntil ? new Date(user.lockedUntil) : null;
  const lockActive = !!lockedUntil && Number.isFinite(lockedUntil.getTime()) && lockedUntil.getTime() > Date.now();
  const attempts = Number(user.failedAttempts ?? 0);

  if (user.status === 'suspended') {
    return {
      tone: 'bad', label: 'Suspended', title: 'Cannot sign in — deactivated',
      detail: 'The account is closed and its sessions were revoked. Any territories and customers this person held were released at deactivation, and reactivating does NOT bring them back — assign them again by hand.',
    };
  }
  if (user.status === 'locked' || lockActive) {
    return {
      tone: 'bad', label: 'Locked', title: 'Sign-in blocked',
      detail: lockActive
        ? `Too many failed sign-in attempts. The lock lifts by itself at ${formatIst(lockedUntil)} — or reactivate the account to clear it now.`
        : 'Too many failed sign-in attempts. Reactivate the account to clear the lock and let them back in.',
    };
  }
  if (!user.lastLoginAt) {
    return {
      tone: 'warn', label: 'Active', title: 'Open, but never signed in',
      detail: 'Nothing is blocking this account — nobody has used it yet. Check the person knows which number is their login: the OTP goes to the mobile above, not to their email.',
    };
  }
  return {
    tone: 'good', label: 'Active', title: 'In good standing',
    detail: attempts > 0
      ? `Signs in normally with an OTP to their mobile. ${attempts} failed attempt${attempts > 1 ? 's' : ''} since the last success — five in a row locks the account for 15 minutes.`
      : 'Signs in normally with an OTP to their mobile. Nothing is locked, suspended or expiring.',
  };
}

/** Tints for the health band. Only tokens the theme actually declares — see styles/theme.css. */
const HEALTH_TONE = {
  good: { band: 'border-success-500/30 bg-success-500/10', ink: 'text-success-600 dark:text-success-500', tag: 'green' as TagTone, icon: <CheckCircle2 className="size-4" /> },
  warn: { band: 'border-warning-500/30 bg-warning-500/10', ink: 'text-warning-600 dark:text-harvest-300', tag: 'gold' as TagTone, icon: <AlertTriangle className="size-4" /> },
  bad: { band: 'border-danger-500/30 bg-danger-500/10', ink: 'text-danger-600 dark:text-danger-500', tag: 'red' as TagTone, icon: <Lock className="size-4" /> },
};

/**
 * Staff detail page (`/staff/:id`) — the read-first counterpart to `/retailers/:id`, opened by every
 * `UserLink` in the app (payments, bank statement, orders).
 *
 * The record EDITS IN PLACE, the way a dealer and a farmer already do: one field at a time, with the
 * pencils drawn from the server's own `editableFields` rather than from role names read here. On a
 * staff record that list is Super-Admin-only (see `user-field-policy.ts`) — every writable field is
 * an access fact, so "fix a typo in a colleague's name" and "re-point their OTP login, or hand
 * yourself their oversight" are the same authority and belong at the same height.
 *
 * The fuller form stays for what a single field cannot express — the territory picker's ⭐ ownership
 * vs coverage, and a Warehouse Manager's site bindings.
 */
export function StaffDetailPage() {
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const hasPerm = usePermissionChecker();
  const canViewAttendance = hasPerm('ATTENDANCE.VIEW');
  // The territory reads are gated separately from USER.VIEW — a viewer holding only USER.VIEW would
  // get a background 403, so don't fire them at all.
  const canViewTerr = hasPerm('TERRITORY.VIEW') || hasPerm('TERRITORY.ASSIGN') || hasPerm('TERRITORY.ASSIGN.HEAD');
  const [editOpen, setEditOpen] = useState(false);
  const [attUser, setAttUser] = useState<AttUser | null>(null);

  const { data: user, isLoading, isError } = useQuery({
    queryKey: ['staff', id],
    queryFn: () => request<any>({ url: `/users/${id}` }),
    enabled: !!id,
  });

  /**
   * Which pencils to draw is the SERVER's answer, published on the record. Reading role names here
   * would put the rule in the one place a client controls, and a user can hold several roles — the
   * PATCH route enforces the same table either way.
   */
  const editableFields = new Set<string>(((user as any)?.editableFields ?? []) as string[]);
  const canEdit = editableFields.size > 0;

  const saveField = async (patch: Record<string, any>) => {
    await request({ url: `/users/${id}`, method: 'PATCH', data: patch });
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['staff', id] }),
      // A role change can release the territories a field user owned, so the book and its counts
      // are stale too — not just the profile field that was edited.
      qc.invalidateQueries({ queryKey: ['staff-territories', id] }),
      qc.invalidateQueries({ queryKey: ['staff-footprint', id] }),
      qc.invalidateQueries({ queryKey: ['users'] }),
    ]);
    message.success('Saved');
  };

  /** `structuralLock` is a reason the field cannot be edited by ANYONE — e.g. a role with no manager. */
  const fieldProps = (field: string, opts?: { confirm?: string; structuralLock?: string }) => ({
    editable: editableFields.has(field) && !opts?.structuralLock,
    lockReason: opts?.structuralLock
      ?? (editableFields.has(field) ? undefined : 'Only a Super Admin can edit a staff record.'),
    confirm: opts?.confirm,
    onSave: (v: any) => saveField({ [field]: v }),
  });

  // "Reports to" is stored as a bare id — resolve the manager's name through the same USER.VIEW read.
  const { data: manager } = useQuery({
    queryKey: ['staff', 'manager', user?.reportsTo],
    queryFn: () => request<any>({ url: `/users/${user.reportsTo}` }),
    enabled: !!user?.reportsTo,
  });

  const { data: assignments } = useQuery({
    queryKey: ['staff-territories', id],
    queryFn: () => request<any[]>({ url: `/territory-assignments/user/${id}` }),
    enabled: !!id && canViewTerr,
  });

  const { data: footprint } = useQuery({
    queryKey: ['staff-footprint', id],
    queryFn: () => request<{ territories: number; retailers: number; farmers: number }>({
      url: `/territory-assignments/user/${id}/footprint`,
    }),
    enabled: !!id && canViewTerr,
  });

  // The staff directory, for the "Reports to" picker. Only fetched when this viewer can actually
  // write the field — an empty picker for a reader is a dead end, and the read costs a round trip.
  const { data: allStaff } = useQuery({
    queryKey: ['staff-for-reports'],
    queryFn: () => requestAllPages('/users'),
    enabled: canEdit,
  });

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>;
  if (isError || !user) {
    return (
      <Result
        status="error"
        title="Couldn't load this staff member"
        subTitle="They may have been removed, or you may not have access."
        extra={<Button variant="primary" onClick={() => navigate('/people?tab=users')}>Back to staff</Button>}
      />
    );
  }

  const roleCode = user.role?.code ?? '';
  const roleName = user.role?.name ?? roleCode ?? '—';
  const extraRoles: any[] = user.additionalRoles ?? [];
  const roleCodes = [roleCode, ...extraRoles.map((r) => r.code)].filter(Boolean);
  const isField = roleCodes.some((c) => FIELD_ROLES.includes(c));

  const perm = (assignments ?? []).filter((a: any) => a.kind !== 'COVERAGE');
  const owned = perm.filter((a: any) => Number(a.isPrimary) === 1);
  const worked = perm.filter((a: any) => Number(a.isPrimary) !== 1);
  const coverage = (assignments ?? []).filter((a: any) => a.kind === 'COVERAGE');

  const wa = waNumber(user.mobile);
  const health = accountHealth(user);
  const healthTone = HEALTH_TONE[health.tone];

  // Only the roles that may actually manage this person, and never themselves.
  const reportsCfg = REPORTS_TO[roleCode];
  const managerOptions = ((allStaff?.data ?? []) as any[])
    .filter((u: any) => reportsCfg?.managerRoles.includes(u.role?.code ?? u.roleCode))
    .filter((u: any) => String(u.id) !== String(user.id))
    .map((u: any) => ({ value: String(u.id), label: u.fullName ?? `#${u.id}` }));

  return (
    <div>
      {/* Header */}
      <Card className="mb-4">
        {/* Wraps on narrow screens — same reason as the retailer hero: the actions Col never shrinks. */}
        <Row align="middle" gutter={16}>
          <Col flex="none">
            <Avatar
              src={user.profilePhotoUrl ?? undefined}
              size={56}
              style={{ background: 'var(--color-brand-600)', fontSize: 22, fontWeight: 600 }}
              fallback={<User size={24} />}
            />
          </Col>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {user.fullName}
            </Typography.Title>
            <Space size={8} wrap style={{ marginTop: 4 }}>
              <Tag tone={ROLE_COLOR[roleCode] ?? 'neutral'}>{roleName}</Tag>
              {extraRoles.map((r) => (
                <Tag key={r.code} tone={ROLE_COLOR[r.code] ?? 'neutral'}>{r.name ?? r.code}</Tag>
              ))}
              <Tag tone={STATUS_COLOR[user.status] ?? 'neutral'}>{STATUS_LABEL[user.status] ?? user.status}</Tag>
              <span style={{ color: token.colorTextSecondary }}>
                {displayMobile(user.mobile)}{user.email ? ` · ${user.email}` : ''}
              </span>
            </Space>
          </Col>
          <Col flex="none">
            <Space wrap size={6}>
              <Tooltip label="Call">
                <Button size="sm" href={`tel:${displayMobile(user.mobile)}`} icon={<Phone size={14} />} aria-label="Call" />
              </Tooltip>
              <Tooltip label="WhatsApp">
                <Button size="sm" href={`https://wa.me/${wa}`} target="_blank" rel="noopener" icon={<MessageCircle size={14} />} aria-label="WhatsApp" />
              </Tooltip>
              {user.email && (
                <Tooltip label="Email">
                  <Button size="sm" href={`mailto:${user.email}`} icon={<Mail size={14} />} aria-label="Email" />
                </Tooltip>
              )}
              {isField && canViewAttendance && (
                <Button
                  size="sm"
                  icon={<CalendarClock size={14} />}
                  onClick={() => setAttUser({ id: String(user.id), name: user.fullName, role: roleCode })}
                >
                  Attendance
                </Button>
              )}
              {/* Single fields are edited in place below; the form is what a field cannot express —
                  the territory picker's ⭐ ownership vs coverage, and a manager's warehouse sites. */}
              {canEdit && (
                <Tooltip label="Edit territories & warehouses">
                  <Button size="sm" icon={<Pencil size={14} />} aria-label="Edit territories and warehouses" onClick={() => setEditOpen(true)} />
                </Tooltip>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Field book — territories owned/covered and the customers on their name. Field roles only. */}
      {isField && canViewTerr && (
        // Field book — moved off antd-compat Card + Statistic onto the shared ui/StatCard, so this
        // band matches every other KPI row in the app (tone wash, icon chip, elevation) instead of
        // being four white boxes with a hand-set 28px figure. Same values.
        <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard label="Territories" value={footprint?.territories ?? 0} tone="sky" icon={<MapPin className="size-4" />} />
          <StatCard label="Owned (primary)" value={owned.length} tone="gold" icon={<Star className="size-4" />} />
          <StatCard label="Retailers" value={footprint?.retailers ?? 0} tone="brand" icon={<Contact className="size-4" />} />
          <StatCard label="Farmers" value={footprint?.farmers ?? 0} tone="success" icon={<User className="size-4" />} />
        </div>
      )}

      <Row gutter={16}>
        <Col xs={24} lg={15}>
          <Card title="Profile">
            <div className="space-y-6">
              <section>
                <SectionH icon={<User className="size-4" />} tint="bg-brand-600/10 text-brand-700 dark:bg-brand-400/10 dark:text-brand-400">Identity</SectionH>
                <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                  <InlineEditField label="Full name" value={user.fullName} {...fieldProps('fullName')}>
                    {user.fullName}
                  </InlineEditField>
                  <InlineEditField
                    label="Photo" type="file" uploadCategory="profile-photo"
                    value={user.profilePhotoUrl} {...fieldProps('profilePhotoUrl')}
                  >
                    {user.profilePhotoUrl
                      ? <DocumentPreview url={user.profilePhotoUrl} label={`${user.fullName} — photo`} />
                      : <span className="font-normal text-subtle">No photo on file</span>}
                  </InlineEditField>
                </dl>
              </section>

              <section>
                <SectionH icon={<Contact className="size-4" />} tint="bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400">Contact</SectionH>
                <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                  <InlineEditField
                    label="Mobile (WhatsApp)" value={user.mobile}
                    {...fieldProps('mobile', {
                      confirm: 'This number IS their login. Changing it re-points OTP sign-in — the new number receives the code, the old one stops working.',
                    })}
                  >
                    <span className="inline-flex items-center gap-2">
                      {displayMobile(user.mobile)}
                      <a href={`tel:${displayMobile(user.mobile)}`} className="text-brand-600 hover:text-brand-700 dark:text-brand-400" aria-label="Call"><Phone className="size-3.5" /></a>
                      <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener" className="text-brand-600 hover:text-brand-700 dark:text-brand-400" aria-label="WhatsApp"><MessageCircle className="size-3.5" /></a>
                    </span>
                  </InlineEditField>
                  <InlineEditField label="Email" value={user.email} placeholder="name@greenbaybio.com" {...fieldProps('email')}>
                    {user.email || undefined}
                  </InlineEditField>
                  <InlineEditField label="Language" value={user.languagePref} type="select" options={LANGUAGE_OPTIONS} {...fieldProps('languagePref')}>
                    {LANGUAGE_NAME[user.languagePref] ?? (String(user.languagePref ?? '').toUpperCase() || undefined)}
                  </InlineEditField>
                </dl>
                {/* The mobile IS the login — say so, since an admin reading this page is often about to change it. */}
                <p className="mt-3 text-xs text-muted">This mobile is their login — they sign in with an OTP sent to it.</p>
              </section>

              <section>
                <SectionH icon={<ShieldCheck className="size-4" />} tint="bg-purple-500/10 text-purple-600 dark:text-purple-400">Role &amp; reporting</SectionH>
                <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                  <InlineEditField
                    label="Primary role" value={roleCode} type="select" options={ROLE_OPTIONS}
                    {...fieldProps('roleCode', {
                      confirm: 'The role IS the permission set — it changes what this person can see and do on their next request. Moving them off a field role also releases the territories they own.',
                    })}
                  >
                    <Tag tone={ROLE_COLOR[roleCode] ?? 'neutral'} style={{ margin: 0 }}>{roleName}</Tag>
                  </InlineEditField>
                  {/* Derived from the legacy multi-role table. One role per user is the rule now, so
                      this is a record of what someone still holds, not something to hand out here. */}
                  <OField label="Additional roles">
                    {extraRoles.length ? extraRoles.map((r) => r.name ?? r.code).join(', ') : undefined}
                  </OField>
                  <InlineEditField
                    label={reportsCfg ? `Reports to (${reportsCfg.managerLabel})` : 'Reports to'}
                    value={user.reportsTo ? String(user.reportsTo) : ''}
                    type="select" options={managerOptions}
                    placeholder={reportsCfg ? `Pick a ${reportsCfg.managerLabel}` : undefined}
                    {...fieldProps('reportsTo', {
                      structuralLock: reportsCfg ? undefined : `A ${roleName} has no reporting line — they answer outside the org chart.`,
                    })}
                  >
                    {manager?.fullName ?? (user.reportsTo ? `#${user.reportsTo}` : undefined)}
                  </InlineEditField>
                </dl>
              </section>

              <section>
                <SectionH icon={<Clock className="size-4" />} tint="bg-sky-500/10 text-sky-600 dark:text-sky-400">Account</SectionH>
                {/*
                  * THE STATUS, SAID OUT LOUD.
                  *
                  * This replaces a "Status" row that held a green pill reading "Active" — true, and
                  * useless: it could not distinguish a login somebody uses every morning from one
                  * created in March that nobody has ever opened, and it said nothing about what to
                  * DO when it wasn't green. The pill is still here, next to the verdict it belongs to.
                  */}
                <div className={cn('mb-5 flex items-start gap-3 rounded-card border p-3', healthTone.band)}>
                  <span className={cn('mt-0.5 shrink-0', healthTone.ink)}>{healthTone.icon}</span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="type-body-strong text-content">{health.title}</span>
                      <Tag tone={healthTone.tag} style={{ margin: 0 }}>{health.label}</Tag>
                    </div>
                    <p className="mt-1 type-caption text-subtle">{health.detail}</p>
                  </div>
                </div>
                <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                  <OField label="Member since">{user.createdAt ? formatIstDate(user.createdAt) : undefined}</OField>
                  <OField label="Last login">{user.lastLoginAt ? formatIst(user.lastLoginAt) : 'Never'}</OField>
                  {/* A handset binding, not a setting — it is cleared by the device-reset action,
                      which demands a reason, so it reads here rather than editing here. */}
                  <OField label="App device">
                    {user.deviceId
                      ? <span className="inline-flex items-center gap-1.5"><Smartphone className="size-3.5 text-subtle" />Bound to one handset</span>
                      : 'Not bound — they have not signed in from the app'}
                  </OField>
                </dl>
              </section>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={9}>
          {/* Money first in this column: "what are they holding" is the question a reader arriving
              from a cash-oversight or wallet row is already asking. The card decides for itself which
              of the two books applies — a wallet is field-only, custody is anyone who holds cash. */}
          {/* The name travels with the figures: the breakdown opens as a dialog over this page, and a
              money table headed "this person" is exactly the ambiguity it exists to remove. */}
          <StaffMoneyCard repId={String(user.id)} isField={isField} repName={user.fullName} />
          <Card title="Territories">
            {!canViewTerr ? (
              <EmptyState compact icon={<MapPin />} title="Not visible to you" description="Viewing territory assignments needs territory permissions." />
            ) : !assignments ? (
              <div className="flex items-center justify-center gap-2 py-6 text-muted"><Spinner /> Loading…</div>
            ) : !assignments.length ? (
              <EmptyState
                compact
                icon={<MapPin />}
                title="No territories"
                description={isField ? 'This staff member has no territory bindings yet.' : 'This role does not work a territory.'}
              />
            ) : (
              <div className="space-y-5">
                {owned.length > 0 && (
                  <section>
                    <div className="mb-2 type-overline text-subtle">Owns ({owned.length})</div>
                    <ul className="space-y-1.5">{owned.map((a: any) => <TerrRow key={a.assignmentId} a={a} owned />)}</ul>
                  </section>
                )}
                {worked.length > 0 && (
                  <section>
                    <div className="mb-2 type-overline text-subtle">Also works ({worked.length})</div>
                    <ul className="space-y-1.5">{worked.map((a: any) => <TerrRow key={a.assignmentId} a={a} />)}</ul>
                  </section>
                )}
                {coverage.length > 0 && (
                  <section>
                    <div className="mb-2 type-overline text-subtle">Covering ({coverage.length})</div>
                    <ul className="space-y-1.5">{coverage.map((a: any) => <TerrRow key={a.assignmentId} a={a} />)}</ul>
                    <p className="mt-2 text-xs text-muted">Temporary cover for an absent colleague.</p>
                  </section>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <UserFormModal open={editOpen} record={user} onClose={() => setEditOpen(false)} />
      <AttendanceUserDrawer user={attUser} onClose={() => setAttUser(null)} />
    </div>
  );
}
